import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    Box, Typography, Paper, Grid, Container, Button,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Chip, TextField, Fade, Avatar, Divider,
    FormControl, InputLabel, Select, MenuItem, CircularProgress,
    IconButton, Tooltip, LinearProgress, InputAdornment,
    Tabs, Tab, Card, CardContent, CardActions,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    Note as NoteIcon,
    Class as ClassIcon,
    Subject as SubjectIcon,
    Description as DescriptionIcon,
    Search as SearchIcon,
    CloudUpload as UploadIcon,
    History as HistoryIcon,
    ArrowBack as ArrowBackIcon,
    FilePresent as FileIcon,
    PictureAsPdf as PdfIcon,
    Description as DocIcon,
    InsertDriveFile as GenericFileIcon,
    Storage as StorageIcon,
    People as PeopleIcon,
    Book as BookIcon
} from '@mui/icons-material';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';
import { useToast } from '../../context/ToastContext';
import { formatDatePart, CustomNepaliDatePicker, getCurrentBSDate } from '../../utils/customDate';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 80px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 60px 0 80px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -60px;
    position: relative;
    z-index: 2;
`;

const PremiumCard = styled(Paper)`
    border-radius: 24px !important;
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.05) !important;
    overflow: hidden;
    margin-bottom: 30px;
`;

const StatCard = styled(Paper)`
    padding: 20px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    background: white;
    border: 1px solid #eef2ff;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 24px -10px rgba(99, 102, 241, 0.15);
    }
`;

const NoteCard = styled(Card)`
    border-radius: 20px !important;
    border: 1px solid #e2e8f0 !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    height: 100%;
    display: flex;
    flex-direction: column;

    &:hover {
        transform: translateY(-8px);
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
        border-color: #6366f1 !important;
    }
`;

const FileIconWrapper = styled(Box)`
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => props.color || '#f1f5f9'};
    color: white;
    margin-bottom: 16px;
`;

const TeacherNotes = () => {
    const { currentUser } = useSelector((state) => state.user);
    const { showToast } = useToast();

    // Tab state
    const [tabValue, setTabValue] = useState(0);

    const [notesList, setNotesList] = useState([]);
    const [classes, setClasses] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClassFilter, setSelectedClassFilter] = useState('all');

    // UI states
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [noteToDelete, setNoteToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Upload state
    const [selectedFile, setSelectedFile] = useState(null);
    const [newNote, setNewNote] = useState({
        title: '',
        description: '',
        sclassID: '',
        subjectID: '',
        date: getCurrentBSDate()
    });

    const teacherId = currentUser?._id;
    const schoolId = currentUser?.school?._id || currentUser?.school;

    useEffect(() => {
        if (teacherId) {
            fetchTeacherClasses();
            fetchNotes();
        }
    }, [teacherId]);

    useEffect(() => {
        if (newNote.sclassID) {
            fetchSubjects(newNote.sclassID);
        }
    }, [newNote.sclassID]);

    const fetchTeacherClasses = async () => {
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/Teacher/Classes/${teacherId}`);
            if (Array.isArray(result.data)) {
                setClasses(result.data);
                if (result.data.length > 0 && !newNote.sclassID) {
                    setNewNote(prev => ({ ...prev, sclassID: result.data[0]._id }));
                }
            }
        } catch (error) {
            console.error('Error fetching classes:', error);
        }
    };

    const fetchSubjects = async (classId) => {
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/Teacher/Subjects/${teacherId}/${classId}`);
            if (Array.isArray(result.data)) {
                setSubjects(result.data);
            } else {
                setSubjects([]);
            }
        } catch (error) {
            console.error('Error fetching subjects:', error);
            setSubjects([]);
        }
    };

    const fetchNotes = async () => {
        setLoading(true);
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/TeacherNotes/${teacherId}`);
            if (Array.isArray(result.data)) {
                setNotesList(result.data);
            } else {
                setNotesList([]);
            }
        } catch (error) {
            console.error('Error fetching notes:', error);
            showToast('Error loading notes', 'error');
        }
        setLoading(false);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleCreateNote = async () => {
        if (!newNote.title || !newNote.description || !selectedFile || !newNote.sclassID) {
            showToast('Please complete all fields and select a file', 'warning');
            return;
        }

        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('title', newNote.title);
            formData.append('description', newNote.description);
            formData.append('teacherID', teacherId);
            formData.append('schoolID', schoolId);
            formData.append('sclassID', newNote.sclassID);
            formData.append('subjectID', newNote.subjectID || '');
            formData.append('date', newNote.date);
            formData.append('file', selectedFile);

            await axios.post(`${process.env.REACT_APP_BASE_URL}/TeacherNoteCreate`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            showToast('Note uploaded successfully!', 'success');
            setTabValue(0); // Switch to list view
            setNewNote({ 
                title: '', 
                description: '', 
                sclassID: classes[0]?._id || '', 
                subjectID: '',
                date: getCurrentBSDate() 
            });
            setSelectedFile(null);
            fetchNotes();
        } catch (error) {
            console.error('Error uploading note:', error);
            showToast('Error uploading note', 'error');
        }
        setSaving(false);
    };

    const handleDeleteClick = (note) => {
        setNoteToDelete(note);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!noteToDelete) return;
        setDeleting(true);
        try {
            await axios.delete(`${process.env.REACT_APP_BASE_URL}/TeacherNote/${noteToDelete._id}`);
            showToast('Note deleted successfully', 'success');
            fetchNotes();
        } catch (error) {
            console.error('Error deleting note:', error);
            showToast('Error deleting note', 'error');
        }
        setDeleting(false);
        setDeleteDialogOpen(false);
        setNoteToDelete(null);
    };

    const handleDownload = (note) => {
        const link = note.filePath;
        if (link) {
            const baseUrl = process.env.REACT_APP_BASE_URL;
            const fullUrl = `${baseUrl}/${link}`;
            window.open(fullUrl, '_blank');
        }
    };

    const getFileIcon = (mimetype) => {
        if (mimetype?.includes('pdf')) return { icon: <PdfIcon />, color: '#ef4444', label: 'PDF' };
        if (mimetype?.includes('word') || mimetype?.includes('document')) return { icon: <DocIcon />, color: '#2563eb', label: 'DOC' };
        if (mimetype?.includes('image')) return { icon: <FileIcon />, color: '#10b981', label: 'IMG' };
        return { icon: <GenericFileIcon />, color: '#64748b', label: 'FILE' };
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const filteredNotes = useMemo(() => {
        let list = notesList;
        if (selectedClassFilter !== 'all') {
            list = list.filter(note => (note.sclass?._id || note.sclass) === selectedClassFilter);
        }
        if (searchTerm) {
            list = list.filter(note => 
                note.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                note.description.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        return list;
    }, [notesList, selectedClassFilter, searchTerm]);

    const stats = useMemo(() => {
        const total = notesList.length;
        const classes = new Set(notesList.map(n => n.sclass?._id || n.sclass)).size;
        const totalSize = notesList.reduce((acc, n) => acc + (n.fileSize || 0), 0);
        return { total, classes, totalSize: formatFileSize(totalSize) };
    }, [notesList]);

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="lg">
                    <Fade in timeout={800}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <IconButton onClick={() => window.history.back()} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                                    <ArrowBackIcon />
                                </IconButton>
                                <Box>
                                    <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
                                        Study Materials
                                    </Typography>
                                    <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400 }}>
                                        Manage and share learning resources with your students.
                                    </Typography>
                                </Box>
                            </Box>
                            
                            <Box sx={{ 
                                bgcolor: 'rgba(255,255,255,0.1)', 
                                p: 0.5, 
                                borderRadius: '16px', 
                                backdropFilter: 'blur(10px)',
                                zIndex: 10,
                                position: 'relative'
                            }}>
                                <Tabs 
                                    value={tabValue} 
                                    onChange={(e, v) => setTabValue(v)}
                                    sx={{ 
                                        minHeight: 'auto',
                                        '& .MuiTabs-indicator': { height: '100%', borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.2)', zIndex: 0 },
                                        '& .MuiTab-root': { minHeight: 'auto', py: 1.5, px: 3, color: 'white', fontWeight: 700, zIndex: 1, textTransform: 'none', borderRadius: '12px', '&.Mui-selected': { color: 'white' } }
                                    }}
                                >
                                    <Tab icon={<BookIcon sx={{ mr: 1 }} />} iconPosition="start" label="All Notes" />
                                    <Tab icon={<UploadIcon sx={{ mr: 1 }} />} iconPosition="start" label="Upload New" />
                                </Tabs>
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            <ContentContainer maxWidth="lg" sx={{mt:2}} >
                {tabValue === 0 ? (
                    <Fade in timeout={500}>
                        <Box>
                            {/* Stats Dashboard */}
                            <Grid container spacing={3} sx={{ mb: 4, mt: 2 }}>
                                <Grid item xs={12} sm={4}>
                                    <StatCard elevation={0}>
                                        <Avatar sx={{ bgcolor: '#e0f2fe', color: '#0ea5e9' }}><NoteIcon /></Avatar>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>TOTAL NOTES</Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.total}</Typography>
                                        </Box>
                                    </StatCard>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <StatCard elevation={0}>
                                        <Avatar sx={{ bgcolor: '#f0fdf4', color: '#10b981' }}><ClassIcon /></Avatar>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>CLASSES COVERED</Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.classes}</Typography>
                                        </Box>
                                    </StatCard>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <StatCard elevation={0}>
                                        <Avatar sx={{ bgcolor: '#fff7ed', color: '#f59e0b' }}><StorageIcon /></Avatar>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>STORAGE USED</Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.totalSize}</Typography>
                                        </Box>
                                    </StatCard>
                                </Grid>
                            </Grid>

                            {/* Action Bar */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                                <Box sx={{ display: 'flex', gap: 2, flexGrow: 1 }}>
                                    <TextField 
                                        placeholder="Search by title or description..." 
                                        size="small" 
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                                        sx={{ width: 350, bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                    />
                                    <FormControl size="small" sx={{ width: 200 }}>
                                        <InputLabel>All Classes</InputLabel>
                                        <Select
                                            value={selectedClassFilter}
                                            label="All Classes"
                                            onChange={(e) => setSelectedClassFilter(e.target.value)}
                                            sx={{ borderRadius: '12px', bgcolor: 'white' }}
                                        >
                                            <MenuItem value="all">All Classes</MenuItem>
                                            {classes.map(cls => (
                                                <MenuItem key={cls._id} value={cls._id}>{cls.sclassName}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Button 
                                    variant="contained" 
                                    startIcon={<AddIcon />} 
                                    onClick={() => setTabValue(1)}
                                    sx={{ borderRadius: '12px', px: 3, bgcolor: '#6366f1', fontWeight: 800, height: 40 }}
                                >
                                    Quick Upload
                                </Button>
                            </Box>

                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
                            ) : filteredNotes.length === 0 ? (
                                <Paper sx={{ p: 10, textAlign: 'center', borderRadius: '24px', border: '2px dashed #e2e8f0', bgcolor: 'transparent' }}>
                                    <NoteIcon sx={{ fontSize: 60, color: '#e2e8f0', mb: 2 }} />
                                    <Typography variant="h5" color="textSecondary" sx={{ fontWeight: 700 }}>No materials found.</Typography>
                                    <Typography color="textSecondary">Upload your first note to share it with your students.</Typography>
                                    <Button variant="outlined" onClick={() => setTabValue(1)} sx={{ mt: 3, borderRadius: '12px' }}>Upload Now</Button>
                                </Paper>
                            ) : (
                                <Grid container spacing={3}>
                                    {filteredNotes.map((note) => {
                                        const fileInfo = getFileIcon(note.fileType);
                                        return (
                                            <Grid item xs={12} sm={6} md={4} key={note._id}>
                                                <NoteCard elevation={0}>
                                                    <CardContent sx={{ p: 3 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                            <FileIconWrapper color={fileInfo.color}>
                                                                {fileInfo.icon}
                                                            </FileIconWrapper>
                                                            <Chip 
                                                                label={fileInfo.label} 
                                                                size="small" 
                                                                sx={{ fontWeight: 800, fontSize: '0.65rem', bgcolor: '#f1f5f9' }} 
                                                            />
                                                        </Box>
                                                        
                                                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: '#1e293b', lineClamp: 1, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                            {note.title}
                                                        </Typography>
                                                        
                                                        <Typography variant="body2" sx={{ color: '#64748b', mb: 2, height: 40, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                                            {note.description}
                                                        </Typography>
                                                        
                                                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                                            <Chip icon={<ClassIcon sx={{ fontSize: '14px !important' }} />} label={note.sclass?.sclassName || 'N/A'} size="small" variant="outlined" sx={{ borderRadius: '8px', fontWeight: 600 }} />
                                                            <Chip icon={<SubjectIcon sx={{ fontSize: '14px !important' }} />} label={note.subject?.subName || 'General'} size="small" variant="outlined" sx={{ borderRadius: '8px', fontWeight: 600 }} />
                                                        </Box>
                                                        
                                                        <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
                                                        
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Box>
                                                                <Typography variant="caption" color="textSecondary" display="block">FILE SIZE</Typography>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{formatFileSize(note.fileSize)}</Typography>
                                                            </Box>
                                                            <Box sx={{ textAlign: 'right' }}>
                                                                <Typography variant="caption" color="textSecondary" display="block">UPLOADED ON</Typography>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{formatDatePart(note.date || note.createdAt)}</Typography>
                                                            </Box>
                                                        </Box>
                                                    </CardContent>
                                                    <CardActions sx={{ p: 2, pt: 0, mt: 'auto', gap: 1 }}>
                                                        <Button 
                                                            fullWidth 
                                                            variant="contained" 
                                                            startIcon={<DownloadIcon />} 
                                                            onClick={() => handleDownload(note)}
                                                            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, bgcolor: '#f1f5f9', color: '#1e293b', boxShadow: 'none', '&:hover': { bgcolor: '#e2e8f0', boxShadow: 'none' } }}
                                                        >
                                                            Download
                                                        </Button>
                                                        <IconButton 
                                                            onClick={() => handleDeleteClick(note)} 
                                                            sx={{ color: '#ef4444', bgcolor: '#fef2f2', borderRadius: '12px', '&:hover': { bgcolor: '#fee2e2' } }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </CardActions>
                                                </NoteCard>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            )}
                        </Box>
                    </Fade>
                ) : (
                    <Fade in timeout={500}>
                        <Box sx={{ mt: 2 }}>
                            <Grid container spacing={4}>
                                <Grid item xs={12} md={5}>
                                    <PremiumCard sx={{ p: 4, height: '100%' }}>
                                        <Typography variant="h5" sx={{ fontWeight: 900, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <UploadIcon color="primary" /> Upload Resource
                                        </Typography>
                                        
                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Title"
                                                    placeholder="e.g., Chapter 1 Summary"
                                                    value={newNote.title}
                                                    onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Description"
                                                    multiline
                                                    rows={4}
                                                    placeholder="Provide a brief summary of what this resource contains..."
                                                    value={newNote.description}
                                                    onChange={(e) => setNewNote({ ...newNote, description: e.target.value })}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Class</InputLabel>
                                                    <Select
                                                        value={newNote.sclassID}
                                                        label="Class"
                                                        onChange={(e) => setNewNote({ ...newNote, sclassID: e.target.value, subjectID: '' })}
                                                        sx={{ borderRadius: '12px' }}
                                                    >
                                                        {classes.map((cls) => (
                                                            <MenuItem key={cls._id} value={cls._id}>{cls.sclassName}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth disabled={!newNote.sclassID}>
                                                    <InputLabel>Subject</InputLabel>
                                                    <Select
                                                        value={newNote.subjectID}
                                                        label="Subject"
                                                        onChange={(e) => setNewNote({ ...newNote, subjectID: e.target.value })}
                                                        sx={{ borderRadius: '12px' }}
                                                    >
                                                        <MenuItem value=""><em>None (General)</em></MenuItem>
                                                        {subjects.map((sub) => (
                                                            <MenuItem key={sub._id} value={sub._id}>{sub.subName}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <CustomNepaliDatePicker
                                                    label="Resource Date"
                                                    value={newNote.date}
                                                    onChange={(date) => setNewNote({ ...newNote, date: date })}
                                                />
                                            </Grid>
                                        </Grid>
                                    </PremiumCard>
                                </Grid>
                                
                                <Grid item xs={12} md={7}>
                                    <PremiumCard sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', border: '2px dashed #cbd5e1 !important', bgcolor: 'transparent !important', boxShadow: 'none !important' }}>
                                        <input
                                            type="file"
                                            id="file-upload"
                                            hidden
                                            onChange={handleFileChange}
                                        />
                                        <label htmlFor="file-upload" style={{ width: '100%', cursor: 'pointer' }}>
                                            <Box sx={{ textAlign: 'center', p: 4 }}>
                                                <Avatar sx={{ width: 80, height: 80, bgcolor: '#eef2ff', color: '#6366f1', mx: 'auto', mb: 3 }}>
                                                    <UploadIcon sx={{ fontSize: 40 }} />
                                                </Avatar>
                                                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                                                    {selectedFile ? selectedFile.name : 'Select Study Material'}
                                                </Typography>
                                                <Typography color="textSecondary" sx={{ mb: 3 }}>
                                                    {selectedFile 
                                                        ? `${formatFileSize(selectedFile.size)} - Click to change` 
                                                        : 'PDF, DOCX, PPT, or ZIP (Max 10MB)'}
                                                </Typography>
                                                {!selectedFile && (
                                                    <Button variant="outlined" component="span" sx={{ borderRadius: '12px', px: 4 }}>
                                                        Browse Files
                                                    </Button>
                                                )}
                                            </Box>
                                        </label>
                                        
                                        {selectedFile && (
                                            <Button 
                                                variant="contained" 
                                                fullWidth 
                                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
                                                onClick={handleCreateNote}
                                                disabled={saving}
                                                sx={{ mt: 2, borderRadius: '12px', py: 2, bgcolor: '#6366f1', fontWeight: 800, maxWidth: 300 }}
                                            >
                                                {saving ? 'Uploading Resource...' : 'Share with Students'}
                                            </Button>
                                        )}
                                    </PremiumCard>
                                </Grid>
                            </Grid>
                        </Box>
                    </Fade>
                )}
            </ContentContainer>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{ sx: { borderRadius: '24px', p: 1, maxWidth: '400px' } }}
            >
                <DialogTitle sx={{ fontWeight: 900, fontSize: '1.5rem', pb: 1 }}>Delete Note</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#475569', fontWeight: 500 }}>
                        Are you sure you want to delete this study material?
                        <Box component="span" sx={{ color: '#1e293b', fontWeight: 800, display: 'block', mt: 1 }}>
                            "{noteToDelete?.title}"
                        </Box>
                        Students will no longer be able to access this file.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ borderRadius: '12px', fontWeight: 700, color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                    <Button 
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
                        sx={{ borderRadius: '12px', fontWeight: 800, textTransform: 'none', px: 3, bgcolor: '#ef4444' }}
                    >
                        {deleting ? 'Deleting...' : 'Confirm Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageWrapper>
    );
};

export default TeacherNotes;
